import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { keepPreviousData } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronDown,
  ChevronRight,
  Download,
  Archive,
  ArchiveRestore,
  ExternalLink,
  Layers,
  Link2,
  Link2Off,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  Printer,
  Replace,
  Search,
  Settings2,
  Trash2,
  Upload,
  Weight,
  X,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import ErrorBoundary from '@/components/ErrorBoundary';
import { PageErrorBoundary } from '@/components/fallbacks';
import { PageLoader } from '@/components/loading/PageLoader';
import { trpc } from '@/lib/trpc';
import { useLocation } from '@/contexts/LocationContext';
import { useRole } from '@/hooks/use-role';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { useOrganization } from '@/contexts/OrganizationContext';
import ProductImageWithHover from '@/features/purchase-orders/components/ProductImageWithHover';
import AddSkuModal from '@/features/inventory/components/AddSkuModal';
import EditSkuModal, { type SkuCatalogItem } from '@/features/inventory/components/EditSkuModal';
import GroupSkusModal from '@/features/inventory/components/GroupSkusModal';
import BulkLinkTrimDialog from '@/features/sku-components/components/BulkLinkTrimDialog';
import AutoGroupModal from '@/features/inventory/components/AutoGroupModal';
import BlankAutoGroupModal from '@/features/inventory/components/BlankAutoGroupModal';
import SkuBulkEditModal from '@/features/inventory/components/SkuBulkEditModal';
import { buildProductsExportRows } from '@/features/inventory/utils/productsExportCsv';
import BatchEditVariantsModal, { type BatchVariant } from '@/features/inventory/components/BatchEditVariantsModal';
import BulkDeleteProductsDialog, {
  type BulkDeleteProductTarget,
} from '@/features/inventory/components/BulkDeleteProductsDialog';
import { ClientCatalogBanner } from '@/features/inventory/components/ClientCatalogBanner';
import { ClientCatalogSwitcher } from '@/features/inventory/components/ClientCatalogSwitcher';
import PrintTechOverrideModal, { type PrintTechVariant } from '@/features/inventory/components/PrintTechOverrideModal';
import ShippingWeightOverrideModal from '@/features/inventory/components/ShippingWeightOverrideModal';
import { SkuStatusBadge } from '@/features/inventory/components/SkuStatusBadge';
import ArchiveSkusDialog from '@/features/inventory/components/ArchiveSkusDialog';
import {
  ReplaceBlankDialog,
  type SharedBlankTarget,
} from '@/features/inventory/components/supply/ReplaceBlankDialog';
import { StopOfferingBlanksDialog } from '@/features/inventory/components/supply/StopOfferingBlanksDialog';
import { SupplyAliasSection } from '@/features/inventory/components/supply/SupplyAliasSection';
import {
  partitionByRetired,
  resolveProductStatus,
  resolveSkuStatus,
  toArchiveTarget,
  type ArchiveSkuTarget,
} from '@/features/inventory/utils/sku-status';
import { formatKg } from '@/lib/parse-kg';
import { useVariantSelection } from '@/features/inventory/hooks/useVariantSelection';
import { getShopifyAdminProductUrl } from '@/lib/shopify-urls';
import { sortVariants } from '@/lib/variant-sort';
import { toCsvContent } from '@/lib/csv';
import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { usePageTitle } from '@/contexts/PageTitleContext';
import { useHeaderLeftSlot } from '@/contexts/HeaderSlotContext';
import { BackToTopPill } from '@/components/BackToTopPill';
import { PageActionBar, PageActionBarDivider } from '@/components/layout/PageActionBar';
import { FloatingSelectionBar } from '@/components/layout/FloatingSelectionBar';
import { useConnectedClientsAtLocations } from '@/features/inventory/hooks/useConnectedClients';
import { useOwnedFloorIds } from '@/features/inventory/hooks/useOwnedFloorIds';
import RoleGuard from '@/components/auth/RoleGuard';
import { ProductsViewSwitch } from '@/features/client-prices/components/ProductsViewSwitch';
import { useProductsViews } from '@/features/client-prices/hooks/useProductsViews';
import { LoadingShell } from '@/components/loading/LoadingShell';
import { CLIENT_PRICES_VIEW, PRODUCTS_VIEW_PARAM } from '@/features/client-prices/lib/client-prices-url';
import {
  BASE_GARMENTS_VIEW,
  WHO_ANY,
  baseGarmentCodeOfSku,
  baseGarmentHref,
  whoToListInput,
  type WhoCanUseFilter as WhoFilter,
} from '@/features/base-garments/lib/base-garments-url';
import { WhoCanUseFilter } from '@/features/base-garments/components/WhoCanUseFilter';
import { ReservedForBadge } from '@/features/base-garments/components/ReservedForBadge';
import { CodeAudienceSelect } from '@/features/base-garments/components/CodeAudienceSelect';
import { parseAudienceValue } from '@/features/base-garments/lib/base-garments-audience';
import { anyBaseGarmentErrorText, parseBaseGarmentError } from '@/features/base-garments/lib/base-garment-errors';
import {
  isBaseGarmentsRefusal,
  papCodeStockTypes,
  papCodesOf,
  papStockTypeCodeOf,
  stockTypeErrorText,
  type PapStockTypeRow,
} from '@/features/base-garments/lib/pap-stock-type';

const colorHexMap: Record<string, string> = {
  'black': '#000000',
  'natural': '#EBE9DF',
  'powder blue': '#0EA5E9',
  'charcoal': '#36454F',
  'lilac': '#9370DB',
  'pale blue': '#B0E0E6',
  'black ringer': '#000000',
  'red ringer': '#FF0000',
  'white': '#FFFFFF',
  'red': '#FF0000',
  'blue': '#0000FF',
  'green': '#008000',
  'yellow': '#FFFF00',
  'purple': '#800080',
  'pink': '#FFC0CB',
  'orange': '#FFA500',
  'brown': '#8B4513',
  'black stone': '#414040',
  'grey stone': '#535252',
  'grey': '#808080',
  'gray': '#808080',
  'navy': '#000080',
  'beige': '#F5F5DC',
  'cream': '#FFFDD0',
  'maroon': '#800000',
  'teal': '#008080',
  'olive': '#808000',
  'silver': '#C0C0C0',
  'gold': '#FFD700',
};

const renderColorCircle = (color: string, sizeClass = 'w-4 h-4') => {
  const isRinger = color.toLowerCase().includes('ringer');
  if (isRinger) {
    const ringerColor = color.toLowerCase().includes('black') ? '#000000' : '#FF0000';
    return (
      <div
        key={color}
        className={`${sizeClass} rounded-full bg-white`}
        style={{ border: `2px solid ${ringerColor}`, boxSizing: 'border-box' }}
        title={color}
      />
    );
  }
  return (
    <div
      key={color}
      className={`${sizeClass} rounded-full border border-border`}
      style={{ backgroundColor: colorHexMap[color.toLowerCase()] || '#CCCCCC' }}
      title={color}
    />
  );
};

const PRODUCTS_PAGE_SIZE = 50;

/** Defensive read of the (parallel-backend) `capsule` field off a product/sku
 *  row. Returns null when absent/blank so the capsule UI stays inert until the
 *  field lands. Module-level so it's referentially stable across renders. */
const capsuleOf = (row: unknown): string | null => {
  const c = (row as { capsule?: string | null } | null | undefined)?.capsule;
  return c && c.trim() !== '' ? c : null;
};

type ProductsTab = 'pod' | 'pap' | 'trims';

/** BLU-534: maps the tab to the listProductsGrouped display filter. 'trims'
 * sends 'TRIM', which the backend maps to skus.is_trim = true — trims keep
 * production_type='PAP' underneath, but get their own tab so PAP stays
 * finished-goods-only. See inventory.dao.ts listUngroupedSkus. */
const productionTypeForTab = (tab: ProductsTab): 'POD' | 'PAP' | 'TRIM' =>
  tab === 'pod' ? 'POD' : tab === 'trims' ? 'TRIM' : 'PAP';

/**
 * Text colour for the filled (3PL override) client-catalog badges. The Badge
 * `default` variant's `text-primary` on its own `bg-primary/10` tint is only
 * 3.87:1 at 10px. `text-primary-strong` (the `--primary-strong` token: the
 * brand hue, darker in light mode and lighter in dark mode) is 7.55:1 on that
 * tint over a white row (7.30:1 over a hovered `bg-muted/40` row), and 8.16:1
 * in dark mode (7.58:1 hovered), so it clears WCAG AA (4.5:1) with room.
 */
const OVERRIDE_BADGE_TEXT = 'text-primary-strong';

/**
 * Client-catalog print-technology badge: ONE value — what a unit of this SKU
 * will actually print as at the caller's floor (server-resolved through the
 * full `resolvePrintTechnology` tier chain), with the winning tier in the
 * hover title. Solid when the caller's own override won, outline otherwise.
 * Print technology is ALWAYS shown for POD in a client catalog: rendering
 * nothing was how 121 untagged MZ SKUs stayed invisible until they printed
 * DTG on day one (the resolver always yields a value, so absence can't
 * happen). PAP has no print technology, so it stays blank.
 */
const TECH_SOURCE_TITLE_KEY = {
  override: 'clientCatalog.techSource.override',
  catalog: 'clientCatalog.techSource.catalog',
  rule: 'clientCatalog.techSource.rule',
  location_default: 'clientCatalog.techSource.locationDefault',
  fallback: 'clientCatalog.techSource.fallback',
} as const;

function ClientTechBadge({
  tech,
  source,
  clientName,
  isPod,
  t,
}: {
  tech: 'DTG' | 'DTF' | null | undefined;
  source: keyof typeof TECH_SOURCE_TITLE_KEY | null | undefined;
  clientName: string;
  isPod: boolean;
  /** The page's `inventory` t, so a long catalog does not subscribe per row. */
  t: TFunction<'inventory'>;
}) {
  if (!isPod || !tech || !source) return null;
  return (
    <Badge
      variant={source === 'override' ? 'default' : 'outline'}
      className={cn('text-[10px] shrink-0 font-sans', source === 'override' && OVERRIDE_BADGE_TEXT)}
      title={t(TECH_SOURCE_TITLE_KEY[source], { client: clientName })}
    >
      {tech}
    </Badge>
  );
}

/**
 * Client-catalog shipping weight badge: the weight a YunExpress label would
 * use for this SKU at the caller's floor (server-resolved through the shared
 * `resolveShippingWeightKg` chain: the 3PL's override, then the client's
 * Shopify weight, then the client's default for the product type), with the
 * winning tier in the hover title. Mirrors `ClientTechBadge`: solid when the
 * caller's own override won, and that state also carries a visible "3PL"
 * marker in its text so it never depends on colour alone. Outline otherwise;
 * amber outline "No weight" when nothing resolves. Shown for every production
 * type (POD blanks and PAP finished goods both ship). The server stamps the
 * same nulls for "nothing resolved" and "no floor to resolve at", so the page
 * mounts this only when the server reports a floor (`badgeLocationId`):
 * without one, a "No weight" would be a guess, not a finding.
 */
const WEIGHT_SOURCE_TITLE_KEY = {
  override: 'clientCatalog.weightSource.override',
  shopify: 'clientCatalog.weightSource.shopify',
  default: 'clientCatalog.weightSource.default',
} as const;

function ClientWeightBadge({
  kg,
  source,
  clientName,
  t,
}: {
  kg: number | null | undefined;
  source: keyof typeof WEIGHT_SOURCE_TITLE_KEY | null | undefined;
  clientName: string;
  /** The page's `inventory` t (see ClientTechBadge). */
  t: TFunction<'inventory'>;
}) {
  if (source && kg != null) {
    const weight = formatKg(kg);
    return (
      <Badge
        variant={source === 'override' ? 'default' : 'outline'}
        className={cn(
          'text-[10px] shrink-0 font-sans tabular-nums',
          source === 'override' && OVERRIDE_BADGE_TEXT,
        )}
        title={t(WEIGHT_SOURCE_TITLE_KEY[source], { client: clientName })}
      >
        {source === 'override'
          ? t('clientCatalog.weightBadge.override', { weight })
          : t('clientCatalog.weightBadge.value', { weight })}
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className="text-[10px] shrink-0 font-sans tabular-nums border-amber-300 text-amber-700"
      title={t('clientCatalog.weightSource.none')}
    >
      {t('clientCatalog.weightBadge.none')}
    </Badge>
  );
}

function ProductCatalogue() {
  const { t } = useTranslation('inventory');
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const { selectedLocation, locations } = useLocation();
  const { organization } = useOrganization();
  const { can, isAdmin } = useRole();
  const { formatConverted } = useCurrencyFormatter();

  usePageTitle(t('products.title'), t('products.subtitle'));

  const [activeTab, setActiveTab] = useState<ProductsTab>('pod');
  // 3PL client-catalog view: null = own catalog. Set = read that client's
  // catalog cross-org (costs/supplier stripped server-side) so the 3PL can set
  // print-technology routing and shipping weights for its own floor. The name
  // is held with the id so the banner and modals keep it while the roster for
  // a newly selected floor loads.
  const [clientOrg, setClientOrg] = useState<{ id: string; name: string } | null>(null);
  const clientOrgId = clientOrg?.id ?? null;
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [capsuleFilter, setCapsuleFilter] = useState<string>('all');
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());
  // "Show archived": own-catalog read filter, applied in SQL on the paged
  // query. Never sent in client view (the server ignores it there too).
  const [includeArchived, setIncludeArchived] = useState(false);
  // "Who can use it" (F25): POD tab, own catalog, 3PL orgs. A query input,
  // so the server filters, counts and pages one set.
  const [whoFilter, setWhoFilter] = useState<WhoFilter>(WHO_ANY);

  // Selection for ungrouped SKUs
  const [selectedSkuIds, setSelectedSkuIds] = useState<Set<string>>(new Set());

  // Modals
  const [editingSku, setEditingSku] = useState<SkuCatalogItem | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  // Add trim (Trims tab). Blanks are created in Base garments (F24).
  const [addSkuOpen, setAddSkuOpen] = useState(false);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [groupSkusOpen, setGroupSkusOpen] = useState(false);
  const [autoGroupOpen, setAutoGroupOpen] = useState(false);
  // POD tab: blanks group themselves by product code; this button catches up the
  // loose ones. Mounted only while open, so the preview reads nothing until then.
  const [blankGroupOpen, setBlankGroupOpen] = useState(false);
  // Phase 92 (UI-SPEC 3.3): the 3PL's bulk Replace over the selected shared
  // blanks. Mounted only while open, so it reads nothing until then.
  const [replaceBlankOpen, setReplaceBlankOpen] = useState(false);
  const [stopOfferingOpen, setStopOfferingOpen] = useState(false);
  // Trims-tab bulk-link target (one trim → many products).
  const [bulkLinkTrim, setBulkLinkTrim] = useState<{ skuId: string; sku: string; title: string | null } | null>(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page and selection when search, tab, or the catalog owner changes.
  // `expandedProducts` is in the list because product ids do not survive a
  // change of org: keeping them would leave stale rows "expanded" against
  // whatever the new page happens to key on.
  useEffect(() => {
    setPage(1);
    setSelectedSkuIds(new Set());
    setCapsuleFilter('all');
    setExpandedProducts(new Set());
    // A client catalog has no Show archived toggle; never carry it across.
    if (clientOrgId !== null) setIncludeArchived(false);
  }, [debouncedSearch, activeTab, clientOrgId, includeArchived, whoFilter]);

  // BLU-1311: active clients at every floor the caller's org owns in view, from
  // the location-scoped roster so Warehouse Coordinators get it too. Same input
  // as useConnectedClients, so the cache is shared with /inventory.
  const sharedLocationsEnabled = (import.meta.env.VITE_SHARED_LOCATIONS_ENABLED ?? '') === 'true';
  const rosterEnabled = sharedLocationsEnabled && can('client_catalog.manage');
  // Products | Base garments | Client prices: drawn when the caller may open
  // at least one view besides this one (Base garments: `skus.manage`; Client
  // prices: `skus.manage` in an org that runs a shared location). Reads only
  // what is already loaded, so it costs no call. Each view gates on the same
  // hook, so the switch never offers a view that refuses.
  const productsViews = useProductsViews();
  const showViewSwitch = productsViews.any;
  const rosterLocationIds = useMemo(
    () =>
      rosterEnabled
        ? (selectedLocation ? [selectedLocation] : locations)
            .filter((l) => l.owner_organization_id === organization?.id)
            .map((l) => l.id)
        : [],
    [rosterEnabled, selectedLocation, locations, organization?.id],
  );
  // One read per owned floor, shared with Base garments; `roster` keeps its
  // identity until a floor's data changes, so the header slot never re-registers.
  const { clients: roster, isLoading: rosterLoading } = useConnectedClientsAtLocations(rosterLocationIds);
  const clientView = clientOrgId !== null;
  const clientName = clientOrg?.name ?? '';
  // Who can use it (F25): only a 3PL has clients to tell apart, only blanks
  // and pick-and-pack codes carry it (POD and PAP tabs, BLU-1436), and only
  // in its own catalog.
  const showWho =
    organization?.is_3pl_provider === true && (activeTab === 'pod' || activeTab === 'pap') && !clientView;
  // BLU-1436: the PAP tab's Stock type column, for whoever may edit the catalog
  // (showWho already means own catalog).
  const showStockType = activeTab === 'pap' && showWho && can('skus.manage');
  const whoInput = showWho ? whoToListInput(whoFilter) : {};
  // The clients across every floor the org owns (the badge names and the
  // filter list), per-floor cached and shared with Base garments.
  const whoLocationIds = useOwnedFloorIds(showWho);
  const { clients: whoClients } = useConnectedClientsAtLocations(whoLocationIds);
  const whoClientName = (id: string | null | undefined): string | null =>
    id ? (whoClients.find((c) => c.id === id)?.name ?? null) : null;
  const clientInRoster = clientOrgId !== null && roster.some((c) => c.id === clientOrgId);
  // While a new floor's roster loads, the selected client stays listed so the
  // switcher never shows an empty value.
  const switcherClients = useMemo(
    () => (rosterLoading && clientOrg && !clientInRoster ? [clientOrg, ...roster] : roster),
    [rosterLoading, clientOrg, clientInRoster, roster],
  );

  // The header switcher is registered BEFORE the loading/error early returns
  // below, so a cross-org FORBIDDEN never strands the operator without a way
  // back to their own catalog.
  const headerLeftSlot = useMemo(
    () => (
      <ClientCatalogSwitcher
        value={clientOrgId}
        onChange={(id) => setClientOrg(id ? switcherClients.find((c) => c.id === id) ?? null : null)}
        clients={switcherClients}
      />
    ),
    [clientOrgId, switcherClients],
  );
  useHeaderLeftSlot(headerLeftSlot);

  // A client not connected at the floor(s) now in view (location switched,
  // junction revoked) drops the page back to the own catalog.
  useEffect(() => {
    if (clientOrgId && !rosterLoading && !clientInRoster) setClientOrg(null);
  }, [clientOrgId, rosterLoading, clientInRoster]);

  const { data, isLoading, error, isPlaceholderData } = trpc.inventory.listProductsGrouped.useQuery({
    productionType: productionTypeForTab(activeTab),
    search: debouncedSearch || undefined,
    page,
    pageSize: PRODUCTS_PAGE_SIZE,
    targetOrganizationId: clientOrgId ?? undefined,
    // Client view: the selected location steers which floor's per-(sku,
    // location) print-tech overrides the badges show (server falls back to
    // the sole junction location when this isn't one).
    locationId: clientOrgId ? selectedLocation?.id : undefined,
    includeArchived: clientOrgId !== null ? undefined : includeArchived || undefined,
    ...whoInput,
  }, {
    placeholderData: keepPreviousData,
    // Held until the roster confirms the client at this floor, so a location
    // switch never fires a client read the server would refuse.
    enabled: clientOrgId === null || clientInRoster,
  });

  // A cross-org read that fails (junction revoked, permission lost) drops the
  // page back to the caller's own catalog instead of parking on a dead error
  // block, and says why.
  useEffect(() => {
    if (!error || !clientOrgId) return;
    setClientOrg(null);
    toast({
      variant: 'destructive',
      title: t('clientCatalog.accessErrorTitle'),
      description: error.message,
    });
  }, [error, clientOrgId, t]);

  const products = useMemo(() => data?.products ?? [], [data]);
  const ungroupedSkus = data?.ungroupedSkus ?? [];
  const totalProducts = data?.totalProducts ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalProducts / PRODUCTS_PAGE_SIZE));

  // Capsule (a Product Dev grouping dimension) surfaced on the catalog. The
  // field arrives on the product/sku DTO from a parallel backend change; read
  // it defensively (module-level `capsuleOf`) so this stays fully inert (no
  // chip, no filter) until then. Filtering is client-side over the loaded page
  // only — the list is server-paginated, so a capsule that spans pages narrows
  // to the visible page (a server-side capsule param is the follow-up).
  const capsuleOptions = useMemo(() => {
    const set = new Set<string>();
    for (const p of data?.products ?? []) {
      const c = capsuleOf(p);
      if (c) set.add(c);
    }
    for (const s of data?.ungroupedSkus ?? []) {
      const c = capsuleOf(s);
      if (c) set.add(c);
    }
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [data]);
  // Memoized: useVariantSelection keys its shift-range anchor + stale-id prune
  // on the items array's IDENTITY — an inline expression would reset both
  // every render.
  const visibleProducts = useMemo(
    () =>
      capsuleFilter === 'all' ? products : products.filter((p) => capsuleOf(p) === capsuleFilter),
    [products, capsuleFilter],
  );
  const visibleUngrouped =
    capsuleFilter === 'all'
      ? ungroupedSkus
      : ungroupedSkus.filter((s) => capsuleOf(s) === capsuleFilter);
  // BLU-1400: the client's copies of its 3PL's garments, split off the same
  // unpaged ungrouped read by the server, in their own read-only section.
  // Shown in full: the capsule filter is inert for every row today
  // (listProductsGrouped sends no `capsule`, so `capsuleOptions` is empty and
  // the picker never shows), and an alias row carries no capsule either.
  const supplyAliasSkus = data?.supplyAliasSkus ?? [];

  const toggleExpand = (productId: string) => {
    setExpandedProducts((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  // BLU-675: product-level multi-select on the grouped table (click,
  // shift-click range, select all) feeding the variant batch-edit modal with
  // the UNION of the selected products' variants. Same hook as ProductDetail's
  // variant selection; selection is page-scoped and pruned automatically when
  // search/tab/capsule filters change the visible list.
  const {
    selectedIds: selectedProductIds,
    toggleSelect: toggleProductSelect,
    selectAll: toggleSelectAllProducts,
    clearSelection: clearProductSelection,
    isAllSelected: allProductsSelected,
    isSomeSelected: someProductsSelected,
    selectedCount: selectedProductCount,
  } = useVariantSelection({ items: visibleProducts });

  const [productBatchOpen, setProductBatchOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  // Product selection must not survive a catalog switch. `keepPreviousData`
  // holds the OUTGOING org's rows on screen while the next catalog loads, so
  // the hook's stale-id prune (which keys on the items array identity) has not
  // fired yet — without this, the routing action could be aimed at the new
  // client with the previous org's sku ids. `clearProductSelection` is a
  // stable useCallback, so this runs on catalog change only.
  useEffect(() => {
    clearProductSelection();
  }, [clientOrgId, clearProductSelection]);

  const selectedBatchVariants = useMemo<BatchVariant[]>(
    () =>
      visibleProducts
        .filter((p) => selectedProductIds.has(p.id))
        .flatMap((p) =>
          p.variants.map((v) => ({ id: v.id, sku: v.sku, isLocked: v.isLocked })),
        ),
    [visibleProducts, selectedProductIds],
  );

  // Bulk-delete targets. Carries variantCount so the confirm copy can state
  // exactly how many variants die with the products, without the dialog
  // re-deriving it from the page query.
  const selectedDeleteTargets = useMemo<BulkDeleteProductTarget[]>(
    () =>
      visibleProducts
        .filter((p) => selectedProductIds.has(p.id))
        .map((p) => ({ id: p.id, title: p.title, variantCount: p.variantCount })),
    [visibleProducts, selectedProductIds],
  );

  // Client view routes BOTH selection surfaces (grouped products and ungrouped
  // SKUs) into the same action, because a client catalog is routinely a mix of
  // the two and "which table did you tick" is not a distinction the operator
  // should have to care about when setting print technology or shipping weight.
  const [printTechOpen, setPrintTechOpen] = useState(false);
  const [shippingWeightOpen, setShippingWeightOpen] = useState(false);
  const sharedLocations = useMemo(() => data?.sharedLocations ?? [], [data]);
  // `badgeLocationId` is the floor the server resolved the client-catalog
  // badges at. Without one every weight field arrives null, which must not
  // read as "No weight" (print tech needs no such gate: its resolver always
  // yields a value at a floor, so null already means "no floor").
  const showWeightBadges = clientView && data?.badgeLocationId != null;

  const routingVariants = useMemo<PrintTechVariant[]>(() => {
    if (!clientView) return [];
    const fromProducts = visibleProducts
      .filter((p) => selectedProductIds.has(p.id))
      .flatMap((p) =>
        p.variants.map((v) => ({
          id: v.id,
          sku: v.sku,
          productionType: v.productionType ?? p.productionType ?? null,
        })),
      );
    const fromUngrouped = visibleUngrouped
      .filter((s) => selectedSkuIds.has(s.id))
      .map((s) => ({ id: s.id, sku: s.sku, productionType: s.productionType ?? null }));
    return [...fromProducts, ...fromUngrouped];
  }, [clientView, visibleProducts, selectedProductIds, visibleUngrouped, selectedSkuIds]);

  // Print technology only routes POD SKUs (PrintTechOverrideModal skips the
  // rest), so a selection without one (the PAP tab, trims) has nothing to
  // route: the bar then hides that action and promotes shipping weight.
  const selectionHasPod = useMemo(
    () => routingVariants.some((v) => v.productionType === 'POD'),
    [routingVariants],
  );

  const clearAllSelections = () => {
    clearProductSelection();
    setSelectedSkuIds(new Set());
  };

  // Archive / Unarchive targets (own catalog only; the buttons are gated on
  // canEditCatalog). Each target carries the SAME fresh Shopify status the
  // row badge uses. Split by retiredAt: live SKUs can be archived, archived
  // ones (visible only with Show archived on) can be unarchived.
  const [archiveDialog, setArchiveDialog] = useState<{
    mode: 'archive' | 'unarchive';
    targets: ArchiveSkuTarget[];
  } | null>(null);

  const productArchive = useMemo(
    () =>
      partitionByRetired(
        visibleProducts
          .filter((p) => selectedProductIds.has(p.id))
          .flatMap((p) => p.variants.map((v) => toArchiveTarget(v, p))),
      ),
    [visibleProducts, selectedProductIds],
  );

  const ungroupedArchive = useMemo(
    () =>
      partitionByRetired(
        visibleUngrouped.filter((s) => selectedSkuIds.has(s.id)).map((s) => toArchiveTarget(s)),
      ),
    [visibleUngrouped, selectedSkuIds],
  );

  // After an Archive / Unarchive pass: SKUs the server refused (they came
  // into use since the check) stay selected, as do the products holding
  // them; everything else is cleared.
  const finishArchive = (refusedIds: string[]) => {
    if (refusedIds.length === 0) {
      clearAllSelections();
      return;
    }
    const refused = new Set(refusedIds);
    setSelectedSkuIds((prev) => new Set([...prev].filter((id) => refused.has(id))));
    for (const p of visibleProducts) {
      if (selectedProductIds.has(p.id) && !p.variants.some((v) => refused.has(v.id))) {
        toggleProductSelect(p.id);
      }
    }
  };

  const invalidate = async () => {
    await utils.inventory.listProductsGrouped.invalidate();
    await utils.inventory.listSkus.invalidate();
    if (selectedLocation) {
      await utils.inventory.getProducts.invalidate({ locationId: selectedLocation.id });
    }
  };

  const toggleSkuSelected = (skuId: string) => {
    setSelectedSkuIds((prev) => {
      const next = new Set(prev);
      if (next.has(skuId)) next.delete(skuId);
      else next.add(skuId);
      return next;
    });
  };

  const toggleAllUngrouped = () => {
    if (selectedSkuIds.size === visibleUngrouped.length) {
      setSelectedSkuIds(new Set());
    } else {
      setSelectedSkuIds(new Set(visibleUngrouped.map((s) => s.id)));
    }
  };

  const handleGrouped = async () => {
    setSelectedSkuIds(new Set());
    await invalidate();
  };

  const [isExporting, setIsExporting] = useState(false);

  const handleExportCsv = async () => {
    setIsExporting(true);

    try {
      // Fetch ALL pages so the export isn't limited to the current page
      const allProducts: typeof products = [];
      let allUngrouped: typeof ungroupedSkus = [];
      const maxPageSize = 100; // schema max
      let exportPage = 1;
      let totalProductCount = 0;

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const result = await utils.inventory.listProductsGrouped.fetch({
          productionType: productionTypeForTab(activeTab),
          search: debouncedSearch || undefined,
          page: exportPage,
          pageSize: maxPageSize,
          targetOrganizationId: clientOrgId ?? undefined,
          // The export is the filtered set on screen, every page of it.
          ...whoInput,
        });

        allProducts.push(...result.products);

        if (exportPage === 1) {
          allUngrouped = result.ungroupedSkus;
          totalProductCount = result.totalProducts;
        }

        if (allProducts.length >= totalProductCount) break;
        exportPage++;
      }

      // Build CSV from all fetched data. Currency is the org currency in the
      // own-org view, and blank in a client view (the caller's currency is
      // not the client's). Never a guessed default.
      const rows = buildProductsExportRows({
        products: allProducts,
        ungroupedSkus: allUngrouped,
        currency: clientView ? '' : (organization?.default_currency ?? '').trim().toUpperCase(),
      });

      const csvContent = toCsvContent(rows);
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      // Client-view exports carry the client in the filename — otherwise three
      // downloads from three catalogs all land as `products-pod.csv`.
      const clientSlug = clientName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      link.setAttribute(
        'download',
        clientSlug ? `products-${activeTab}-${clientSlug}.csv` : `products-${activeTab}.csv`,
      );
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      toast({
        title: t('products.exportFailed'),
        description: err instanceof Error ? err.message : t('products.exportFailedDesc'),
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  /** BLU-711: `is_shared` reaches the flat SKU list from `listSkus`; the
   *  grouped-variant projection may not carry it, so read it defensively
   *  (same cast pattern as shopifyInventoryItemId below) and treat a missing
   *  flag as "not shared" rather than rendering a wrong chip. */
  const isSharedSku = (row: unknown): boolean =>
    Boolean((row as { isShared?: boolean }).isShared);

  // Phase 92 (UI-SPEC 3.3, D-12): the selected blanks the caller shares, for
  // the bulk Replace and Stop offering. BOTH selection surfaces feed one set:
  // owner blanks are often ungrouped, and which table was ticked is not a
  // distinction the operator should have to care about (the client-view
  // routing above works the same way). Each bar offers the two verbs only
  // when its OWN selection holds a shared blank; either bar opens the dialogs
  // on the whole set, which they list. From the page data already on screen;
  // no read of its own.
  const sharedBlanksFromProducts = useMemo<SharedBlankTarget[]>(
    () =>
      visibleProducts
        .filter((p) => selectedProductIds.has(p.id))
        .flatMap((p) => p.variants)
        .filter((v) => Boolean((v as { isShared?: boolean }).isShared))
        .map((v) => ({ id: v.id, sku: v.sku, size: v.size ?? null, color: v.color ?? null })),
    [visibleProducts, selectedProductIds],
  );
  const sharedBlanksFromUngrouped = useMemo<SharedBlankTarget[]>(
    () =>
      visibleUngrouped
        .filter((s) => selectedSkuIds.has(s.id))
        .filter((s) => Boolean((s as { isShared?: boolean }).isShared))
        .map((s) => ({ id: s.id, sku: s.sku, size: s.size ?? null, color: s.color ?? null })),
    [visibleUngrouped, selectedSkuIds],
  );
  const selectedSharedBlanks = useMemo(
    () => [...sharedBlanksFromProducts, ...sharedBlanksFromUngrouped],
    [sharedBlanksFromProducts, sharedBlanksFromUngrouped],
  );

  // F24: a blank has ONE home. Its pencil (and its row, when it has one)
  // opens it in Base garments; a blank whose SKU is not base-garment shaped
  // is not listed there, and a caller who can't open Base garments would be
  // turned away from it, so both keep the old editor.
  const blankHref = (row: { sku: string; isLocked?: boolean | null; isTrim?: boolean | null; productionType?: string | null }) => {
    if (!productsViews.baseGarments || clientView || activeTab !== 'pod' || row.isLocked || row.isTrim) return null;
    if ((row.productionType ?? 'POD') !== 'POD') return null;
    const code = baseGarmentCodeOfSku(row.sku);
    return code ? baseGarmentHref(code) : null;
  };

  // A product made only of blanks opens in Base garments too (X3), by the
  // same rule as each blank's pencil; anything else keeps its product page.
  const productHref = (product: typeof products[number]): string => {
    const hrefs = product.variants.map((variant) => blankHref(variant));
    const [first] = hrefs;
    return first && hrefs.every((href) => href !== null) ? first : `/products/${product.id}`;
  };

  const openEditVariant = (variant: typeof products[number]['variants'][number], productType: string | null) => {
    const href = blankHref(variant);
    if (href) {
      navigate(href);
      return;
    }
    setEditingSku({
      id: variant.id,
      sku: variant.sku,
      title: variant.title,
      category: {
        type: variant.category ?? productType ?? null,
        color: variant.color ?? null,
        size: variant.size ?? null,
      },
      imageUrl: variant.imageUrl,
      productionType: activeTab === 'pap' ? 'PAP' : 'POD',
      isLocked: variant.isLocked,
      skuCostOverride: variant.skuCostOverride,
      skuCostOverrideCurrency: variant.skuCostOverrideCurrency,
      skuCostOverrideOrg: variant.skuCostOverrideOrg,
      hsCode: null,
      weightValue: null,
      weightUnit: null,
      supplierId: variant.supplierId ?? null,
      isShared: isSharedSku(variant),
      shopifyInventoryItemId: (variant as Record<string, unknown>).shopifyInventoryItemId as string | null ?? null,
      erpManagedFields: (variant as Record<string, unknown>).erpManagedFields as Record<string, { managedAt: string; managedBy: string }> | undefined,
    });
    setEditOpen(true);
  };

  const openEditUngrouped = (sku: typeof ungroupedSkus[number]) => {
    const href = blankHref(sku);
    if (href) {
      navigate(href);
      return;
    }
    setEditingSku({
      id: sku.id,
      sku: sku.sku,
      title: sku.title,
      category: {
        type: sku.category ?? null,
        color: sku.color ?? null,
        size: sku.size ?? null,
      },
      imageUrl: sku.imageUrl,
      productionType: (sku.productionType ?? 'POD') as 'POD' | 'PAP',
      isTrim: sku.isTrim ?? false,
      isLocked: sku.isLocked,
      skuCostOverride: sku.skuCostOverride,
      skuCostOverrideCurrency: sku.skuCostOverrideCurrency,
      skuCostOverrideOrg: sku.skuCostOverrideOrg,
      hsCode: null,
      weightValue: null,
      weightUnit: null,
      supplierId: sku.supplierId ?? null,
      isShared: isSharedSku(sku),
      shopifyInventoryItemId: (sku as Record<string, unknown>).shopifyInventoryItemId as string | null ?? null,
      erpManagedFields: (sku as Record<string, unknown>).erpManagedFields as Record<string, { managedAt: string; managedBy: string }> | undefined,
    });
    setEditOpen(true);
  };

  const variantEditLabel = (variant: { sku: string; isLocked?: boolean | null; productionType?: string | null }) =>
    blankHref(variant) ? t('products.openInBaseGarments') : t('products.editVariant');
  const skuEditLabel = (sku: { sku: string; isLocked?: boolean | null; isTrim?: boolean | null; productionType?: string | null }) =>
    blankHref(sku) ? t('products.openInBaseGarments') : t('editSkuModal.editSku');

  // BLU-1436: each PAP code's stock type, from the rows on screen (no read of its
  // own). Archived, trim and alias rows take no part (the save skips them too).
  const papStockTypes = useMemo(() => {
    if (!showStockType || !data) return new Map<string, string>();
    const rows: PapStockTypeRow[] = [...data.products.flatMap((p) => p.variants), ...data.ungroupedSkus];
    return papCodeStockTypes(rows);
  }, [showStockType, data]);
  // Codes the server sent to Base garments (they have SKUs that are not pick and
  // pack): their picker gives way to the link until a reload.
  const [refusedCodes, setRefusedCodes] = useState<ReadonlySet<string>>(() => new Set());
  // The reload is returned, so the picker stays in its saving state until the new
  // value lands (and the toast survives the page unmounting mid-save).
  const setPapCodeAudience = trpc.baseGarments.setPapCodeAudience.useMutation({
    retry: false,
    onSuccess: (result, input) => {
      toast({ title: t('products.stockType.saved', { count: result.skuCount, code: input.code }) });
      return Promise.all([invalidate(), utils.baseGarments.invalidate()]);
    },
  });
  const savingCode = setPapCodeAudience.isPending ? (setPapCodeAudience.variables?.code ?? null) : null;

  const saveStockType = (code: string, value: string) => {
    const choice = parseAudienceValue(value);
    setPapCodeAudience.mutate(
      {
        code,
        audience: choice.audience,
        ...(choice.clientOrganizationId ? { clientOrganizationId: choice.clientOrganizationId } : {}),
      },
      {
        onError: (err) => {
          const info = parseBaseGarmentError(err);
          const toBaseGarments = isBaseGarmentsRefusal(info);
          if (toBaseGarments) setRefusedCodes((prev) => new Set(prev).add(code));
          const href = baseGarmentHref(code);
          toast({
            variant: 'destructive',
            title: t('products.stockType.saveFailed'),
            description: info ? stockTypeErrorText(t, info, code) : anyBaseGarmentErrorText(t, err, code),
            duration: Infinity,
            action:
              toBaseGarments && productsViews.baseGarments ? (
                <ToastAction altText={t('products.openInBaseGarments')} onClick={() => navigate(href)}>
                  {t('products.openInBaseGarments')}
                </ToastAction>
              ) : undefined,
          });
        },
      },
    );
  };

  /** One code's Stock type cell: the picker, the Base garments link, or a dash that says why. */
  const renderStockType = (code: string | null, codeless: boolean) => {
    if (data?.reservedForDegraded) {
      return (
        <span className="text-muted-foreground" title={t('products.stockType.unavailable')}>
          <span aria-hidden="true">-</span>
          <span className="sr-only">{t('products.stockType.unavailable')}</span>
        </span>
      );
    }
    if (code === null) {
      if (!codeless) return null;
      return (
        <span className="text-muted-foreground" title={t('products.stockType.noCode')}>
          <span aria-hidden="true">-</span>
          <span className="sr-only">{t('products.stockType.noCode')}</span>
        </span>
      );
    }
    if (refusedCodes.has(code) && productsViews.baseGarments) {
      return (
        <Link
          to={baseGarmentHref(code)}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
        >
          {t('products.openInBaseGarments')}
        </Link>
      );
    }
    const value = papStockTypes.get(code);
    if (value === undefined) return null;
    return (
      <CodeAudienceSelect
        code={code}
        value={value}
        clients={whoClients}
        onChange={(value) => saveStockType(code, value)}
        saving={savingCode === code}
        disabled={savingCode !== null && savingCode !== code}
      />
    );
  };
  /** A live PAP row whose SKU starts with no product code: its dash says why. */
  const isCodeless = (row: PapStockTypeRow) =>
    !row.retiredAt && !row.isTrim && !row.isSupplyAlias && row.productionType === 'PAP' && papStockTypeCodeOf(row) === null;

  if (isLoading) return <PageLoader pool="products" />;

  if (error) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4 max-w-md">
          <h2 className="text-xl font-semibold text-red-700 mb-2">{t('products.errorLoading')}</h2>
          <p className="text-red-600">{error.message}</p>
        </div>
      </div>
    );
  }

  const isPap = activeTab === 'pap';
  // Gates selection and the bulk bar; in client view, the permission the
  // cross-org override writes check (BLU-1311).
  const canManage = can(clientView ? 'client_catalog.manage' : 'skus.manage');
  const isTrims = activeTab === 'trims';
  // In client view every catalog affordance except selection belongs to the
  // client, so it is hidden rather than disabled — a wall of dead buttons is
  // worse than a shorter bar. The two edit entry points that DO stay visible
  // (the row pencils) are disabled with a reason, because their absence would
  // read as "this SKU is broken".
  const canEditCatalog = !clientView && can('skus.manage');
  // Default cost shows for skus.costs_manage holders (own catalog).
  const canSeeSkuCosts = !clientView && can('skus.costs_manage');
  const managedByClient = t('clientCatalog.managedBy', { client: clientName });

  return (
    <div className="flex flex-col">
      {/* Full-width list-page padding (mirrors OrdersList). The responsive
          px-4 sm:px-6 lg:px-8 exactly cancels PageActionBar's
          -mx-4 sm:-mx-6 lg:-mx-8 breakout at every breakpoint — a flat px-6
          overshot the bar's lg:-mx-8 by 8px/side, forcing a spurious
          page-level horizontal scrollbar. */}
      <div className="w-full py-6 px-4 sm:px-6 lg:px-8">
        <ErrorBoundary
          fallback={
            <div className="bg-card rounded-lg border p-8 text-center">
              <h3 className="text-lg font-semibold mb-2">{t('products.errorLoadingDesc').split('.')[0]}</h3>
              <p className="text-muted-foreground mb-4">
                {t('products.errorLoadingDesc')}
              </p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
              >
                {t('reloadPage')}
              </button>
            </div>
          }
        >
          <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as ProductsTab)}
            className="w-full"
          >
            <PageActionBar
              ariaLabel="Products actions"
              left={
                <>
                  {showViewSwitch && (
                    <>
                      <ProductsViewSwitch value="catalogue" />
                      <PageActionBarDivider variant="filter" />
                    </>
                  )}
                  <TabsList className="h-8 grid grid-cols-3 w-[270px]">
                    <TabsTrigger value="pod" className="h-6 text-xs">POD</TabsTrigger>
                    <TabsTrigger value="pap" className="h-6 text-xs">PAP</TabsTrigger>
                    <TabsTrigger value="trims" className="h-6 text-xs">Trims</TabsTrigger>
                  </TabsList>
                  {!isLoading && (
                    <span className="self-center text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                      {t('products.productCount', { count: totalProducts })}
                    </span>
                  )}
                  <PageActionBarDivider />
                  <div className="relative w-72">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder={t('products.searchPlaceholder')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                      className="h-8 pl-9 text-xs"
                    />
                  </div>
                  {/* Capsule filter — only shown when the loaded page carries any
                      capsule values, so single-tenant / pre-backfill views are
                      byte-identical to today. */}
                  {capsuleOptions.length > 0 && (
                    <Select value={capsuleFilter} onValueChange={setCapsuleFilter}>
                      <SelectTrigger className="h-8 w-[160px] text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('products.allCapsules')}</SelectItem>
                        {capsuleOptions.map((c) => (
                          <SelectItem key={c} value={c}>
                            {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {showWho && (
                    <WhoCanUseFilter value={whoFilter} onChange={setWhoFilter} clients={whoClients} />
                  )}
                  {/* Show archived: a READ filter for every own-catalog viewer,
                      applied in SQL on the paged query. Hidden in client view
                      (the server ignores it cross-org too). */}
                  {!clientView && (
                    <div className="flex h-8 items-center gap-2">
                      <Switch
                        id="products-show-archived"
                        checked={includeArchived}
                        onCheckedChange={setIncludeArchived}
                      />
                      <label
                        htmlFor="products-show-archived"
                        className="cursor-pointer select-none whitespace-nowrap text-xs text-muted-foreground"
                      >
                        {t('products.showArchived')}
                      </label>
                    </div>
                  )}
                </>
              }
              right={
                <>
                  {isTrims && canEditCatalog && (
                    <Button
                      size="sm"
                      onClick={() => setAddSkuOpen(true)}
                      className="h-8 px-3 text-xs gap-1.5"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      {t('products.addTrim')}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExportCsv}
                    disabled={isExporting || (products.length === 0 && ungroupedSkus.length === 0)}
                    className="h-8 px-3 text-xs gap-1.5"
                  >
                    {isExporting ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                    ) : (
                      <Download className="h-3.5 w-3.5" />
                    )}
                    {isExporting ? t('products.exporting') : t('products.exportCsv')}
                  </Button>
                  {/* Round trip: Export, fill in Default cost, size or
                      color, Bulk edit CSV. Every tab; own catalog only
                      (hidden in client view). */}
                  {canEditCatalog && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setBulkEditOpen(true)}
                      className="h-8 px-3 text-xs gap-1.5"
                    >
                      <Upload className="h-3.5 w-3.5" />
                      {t('products.bulkEditCsv')}
                    </Button>
                  )}
                  {/* BLU-534: grouping a trim into a "product" has no clean
                      home — the resulting product would carry whatever
                      production_type we pick, not is_trim, so it'd surface
                      under POD or PAP rather than Trims. Hidden here rather
                      than forced (trims are stockable components, not
                      multi-variant product families). */}
                  {!isTrims && canEditCatalog && ungroupedSkus.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => (isPap ? setAutoGroupOpen(true) : setBlankGroupOpen(true))}
                      className="h-8 px-3 text-xs gap-1.5"
                    >
                      <Layers className="h-3.5 w-3.5" />
                      {isPap ? t('products.autoGroup') : t('products.groupBlanks')}
                    </Button>
                  )}
                  {/* Blanks are added in Base garments (F24), so the menu holds
                      only the Admin sync settings. */}
                  {isAdmin() && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 w-8 p-0"
                          aria-label={t('products.moreActions')}
                        >
                          <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {/* /stock-sync is Admin-only (RoleGuard). */}
                        {isAdmin() && (
                          <DropdownMenuItem onClick={() => navigate('/stock-sync')}>
                            <Settings2 className="mr-2 h-4 w-4" />
                            {t('products.syncSettings')}
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </>
              }
            />

            {/* Cross-org context. Sits below the sticky bar (see the component
                docblock) and carries the authority boundary, not just the
                identity. No per-row purple ribbons: this view is single-org by
                construction, so DESIGN.md §13.8 suppression applies. */}
            {/* mode="wait" per DESIGN.md §13.8: switching straight from one
                client to another swaps the banner instead of briefly stacking
                two of them in flow. */}
            <AnimatePresence initial={false} mode="wait">
              {clientView && (
                <ClientCatalogBanner
                  key={clientOrgId}
                  clientName={clientName}
                  onReset={() => setClientOrg(null)}
                />
              )}
            </AnimatePresence>

            {/* keepPreviousData keeps the OUTGOING org's rows on screen while
                the next catalog loads. Dim + aria-busy so they never read as
                the client you just switched to. */}
            <TabsContent
              value={activeTab}
              aria-busy={isPlaceholderData}
              className={cn(
                'mt-6 transition-opacity duration-200 motion-reduce:transition-none',
                isPlaceholderData && 'opacity-60',
              )}
            >
                {visibleProducts.length === 0 &&
                visibleUngrouped.length === 0 &&
                supplyAliasSkus.length === 0 ? (
                  <div className="rounded-md border p-12 text-center text-muted-foreground">
                    <p className="text-lg font-medium mb-1">{t('products.noProducts')}</p>
                    <p className="text-sm">
                      {debouncedSearch
                        ? t('products.noResultsFor', { search: debouncedSearch })
                        : showWho && whoFilter.kind !== 'any'
                          ? isPap
                            ? t('products.stockType.noMatch')
                            : t('products.noWhoMatch')
                          : clientView
                          // Every own-catalog empty state tells the operator to
                          // create, import or sync. None of those are theirs to
                          // do here, so client view gets the one true sentence.
                          ? t('crossOrg.modal.skuEmptyCatalogHelp', { client: clientName })
                          : isTrims
                            ? t('products.emptyTrims')
                            : isPap
                              ? t('products.emptyPap')
                              : t('products.emptyPod')}
                    </p>
                    {showWho && whoFilter.kind !== 'any' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setWhoFilter(WHO_ANY)}
                        className="mt-4 h-8 px-3 text-xs"
                      >
                        {t('baseGarments.view.clearFilters')}
                      </Button>
                    )}
                    {isTrims && !debouncedSearch && canEditCatalog && (
                      <Button
                        size="sm"
                        onClick={() => setAddSkuOpen(true)}
                        className="mt-4 h-8 px-3 text-xs gap-1.5"
                      >
                        <Plus className="h-3.5 w-3.5" />
                        {t('products.addTrim')}
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Products table */}
                    {visibleProducts.length > 0 && (
                      <div className="rounded-md border">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              {canManage && (
                                <TableHead className="w-10 px-3">
                                  <Checkbox
                                    checked={
                                      allProductsSelected
                                        ? true
                                        : someProductsSelected
                                          ? 'indeterminate'
                                          : false
                                    }
                                    onCheckedChange={toggleSelectAllProducts}
                                    aria-label={t('products.bulkBar.selectAllAria')}
                                  />
                                </TableHead>
                              )}
                              <TableHead className="w-10" />
                              <TableHead className="w-16">{t('table.image')}</TableHead>
                              <TableHead>{t('product')}</TableHead>
                              <TableHead className="w-20">{t('size')}</TableHead>
                              <TableHead className="w-24">{t('color')}</TableHead>
                              <TableHead className="w-32">{t('productDetail.variants', { count: 0 }).split(' (')[0]}</TableHead>
                              {showStockType && <TableHead className="w-48">{t('baseGarments.table.whoCanUse')}</TableHead>}
                              {isPap && <TableHead className="w-24 text-right">Shopify</TableHead>}
                              <TableHead className="w-10" />
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {visibleProducts.map((product) => {
                              const isExpanded = expandedProducts.has(product.id);
                              const productStatus = resolveProductStatus({
                                shopifyStatus: product.shopifyStatus,
                                productionType: product.productionType,
                                variants: product.variants,
                              });
                              // Client view: the link would splice OUR shopify
                              // domain onto THEIR product id, landing the
                              // operator on a 404 in the wrong store.
                              const shopifyUrl = isPap && !clientView
                                ? getShopifyAdminProductUrl(organization?.shopify_domain, product.shopifyProductId)
                                : null;
                              // BLU-1436: one code = the picker on the product row;
                              // several = one picker per variant row.
                              const productCodes = showStockType ? papCodesOf(product.variants) : [];

                              return (
                                <>
                                  {/* Product row */}
                                  <TableRow
                                    key={product.id}
                                    className={cn(
                                      'hover:bg-muted/40',
                                      !clientView && 'cursor-pointer',
                                      productStatus === 'archived' && 'text-muted-foreground',
                                    )}
                                    data-state={selectedProductIds.has(product.id) ? 'selected' : undefined}
                                    onClick={
                                      clientView ? undefined : () => navigate(productHref(product))
                                    }
                                  >
                                    {canManage && (
                                      <TableCell className="px-3" onClick={(e) => e.stopPropagation()}>
                                        <Checkbox
                                          checked={selectedProductIds.has(product.id)}
                                          onCheckedChange={() => toggleProductSelect(product.id)}
                                          onClick={(e) => {
                                            if (e.shiftKey) {
                                              e.preventDefault();
                                              toggleProductSelect(product.id, e);
                                            }
                                          }}
                                          aria-label={t('products.bulkBar.selectProductAria', {
                                            title: product.title || t('products.untitledProduct'),
                                          })}
                                        />
                                      </TableCell>
                                    )}
                                    <TableCell
                                      className="p-2 text-center"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleExpand(product.id);
                                      }}
                                    >
                                      {isExpanded ? (
                                        <ChevronDown className="h-4 w-4 text-muted-foreground mx-auto" />
                                      ) : (
                                        <ChevronRight className="h-4 w-4 text-muted-foreground mx-auto" />
                                      )}
                                    </TableCell>
                                    <TableCell className="p-2">
                                      <ProductImageWithHover
                                        imageUrl={product.imageUrl ?? undefined}
                                        productSku={product.title ?? ''}
                                        size="large"
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <div>
                                        <div className="flex items-center gap-2">
                                          <span className="font-medium truncate max-w-[300px]">
                                            {product.title || t('products.untitledProduct')}
                                          </span>
                                          <Badge variant="outline" className="text-[10px] shrink-0">
                                            {/* BLU-534: is_trim lives on skus, not products — grouped
                                                trim products shouldn't exist going forward (Auto-Group/
                                                Group-into-Product are hidden on the Trims tab), but this
                                                covers any pre-existing edge case defensively. */}
                                            {product.variants.length > 0 && product.variants.every((v) => v.isTrim)
                                              ? 'Trim'
                                              : product.productionType}
                                          </Badge>
                                          {product.variants.some(v => v.isPreprinted) && (
                                            <span className="inline-flex items-center rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary shrink-0">
                                              Pre-printed
                                            </span>
                                          )}
                                          <SkuStatusBadge status={productStatus} />
                                          {capsuleOf(product) && (
                                            <span
                                              title={t('products.capsuleLabel')}
                                              className="inline-flex max-w-[16ch] shrink-0 items-center truncate rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium leading-none text-muted-foreground"
                                            >
                                              {capsuleOf(product)}
                                            </span>
                                          )}
                                        </div>
                                        {!isPap && (() => {
                                          const uniqueColors = [...new Set(
                                            product.variants
                                              .map(v => v.color)
                                              .filter((c): c is string => !!c)
                                          )].sort();
                                          return uniqueColors.length > 0 ? (
                                            <div className="flex flex-wrap gap-1 mt-1">
                                              {uniqueColors.map(color => renderColorCircle(color, 'w-3 h-3'))}
                                            </div>
                                          ) : null;
                                        })()}
                                      </div>
                                    </TableCell>
                                    <TableCell />
                                    <TableCell />
                                    <TableCell>
                                      <Badge variant="secondary" className="text-xs">
                                        {t('products.bulkBar.variantCount', { count: product.variantCount })}
                                      </Badge>
                                    </TableCell>
                                    {showStockType && (
                                      <TableCell onClick={(e) => e.stopPropagation()}>
                                        {productCodes.length > 1 ? (
                                          <span
                                            className="text-xs text-muted-foreground"
                                            title={t('products.stockType.codesHint')}
                                          >
                                            {t('products.stockType.codes', { count: productCodes.length })}
                                            <span className="sr-only">. {t('products.stockType.codesHint')}</span>
                                          </span>
                                        ) : (
                                          renderStockType(productCodes[0] ?? null, product.variants.some(isCodeless))
                                        )}
                                      </TableCell>
                                    )}
                                    {isPap && (
                                      <TableCell className="text-right">
                                        {shopifyUrl ? (
                                          <a
                                            href={shopifyUrl}
                                            target="_blank"
                                            rel="noreferrer noopener"
                                            onClick={(e) => e.stopPropagation()}
                                            className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                                          >
                                            Open
                                            <ExternalLink className="h-3.5 w-3.5" />
                                          </a>
                                        ) : (
                                          '-'
                                        )}
                                      </TableCell>
                                    )}
                                  </TableRow>

                                  {/* Variant sub-rows */}
                                  {isExpanded &&
                                    sortVariants(product.variants).map((variant) => {
                                      const variantStatus = resolveSkuStatus({
                                        retiredAt: variant.retiredAt,
                                        shopifyStatus: product.shopifyStatus,
                                        productionType: variant.productionType,
                                        isLocked: variant.isLocked,
                                        isPreprinted: variant.isPreprinted,
                                      });
                                      return (
                                      <TableRow
                                        key={variant.id}
                                        className={cn(
                                          'bg-muted/20 hover:bg-muted/40',
                                          variantStatus === 'archived' && 'text-muted-foreground',
                                        )}
                                      >
                                        {canManage && <TableCell />}
                                        <TableCell />
                                        <TableCell className="p-2">
                                          <ProductImageWithHover
                                            imageUrl={variant.imageUrl ?? product.imageUrl ?? undefined}
                                            productSku={variant.sku}
                                            size="large"
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <div>
                                            <span className="inline-flex items-center gap-1.5">
                                              <span className="font-mono text-sm">{variant.sku}</span>
                                              {/* Exception only: a marker the product row
                                                  already shows would repeat on every size. */}
                                              {variantStatus !== productStatus && (
                                                <SkuStatusBadge status={variantStatus} />
                                              )}
                                              {/* BLU-711: see the flat-SKU table below —
                                                  same neutral outline chip. */}
                                              {/* F25: a blank kept for one client says so. */}
                                              {showStockType && papStockTypeCodeOf(variant) !== null ? null : variant.reservedForOrganizationId ? (
                                                <ReservedForBadge
                                                  clientName={whoClientName(variant.reservedForOrganizationId)}
                                                  className="shrink-0"
                                                />
                                              ) : isSharedSku(variant) && (
                                                <Badge
                                                  variant="outline"
                                                  className="gap-1 text-[10px] shrink-0"
                                                  title={t('sharedSkus.badgeTitle')}
                                                >
                                                  <Link2 className="h-2.5 w-2.5" aria-hidden="true" />
                                                  {t('sharedSkus.badge')}
                                                </Badge>
                                              )}
                                              {variant.isPreprinted && (
                                                <span className="inline-flex items-center rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                                                  Pre-printed
                                                </span>
                                              )}
                                              {clientView && (
                                                <ClientTechBadge
                                                  tech={variant.printTechnologyResolved}
                                                  source={variant.printTechnologySource}
                                                  clientName={clientName}
                                                  // null production_type routes as POD in batching — badge must agree.
                                                  isPod={(variant.productionType ?? 'POD') === 'POD'}
                                                  t={t}
                                                />
                                              )}
                                              {showWeightBadges && (
                                                <ClientWeightBadge
                                                  kg={variant.shippingWeightKg}
                                                  source={variant.shippingWeightSource}
                                                  clientName={clientName}
                                                  t={t}
                                                />
                                              )}
                                            </span>
                                            {variant.title && (
                                              <div className="text-muted-foreground text-xs truncate max-w-[200px]">
                                                {variant.title}
                                              </div>
                                            )}
                                          </div>
                                        </TableCell>
                                        <TableCell className="text-sm">
                                          {variant.size || '-'}
                                        </TableCell>
                                        <TableCell className="text-sm">
                                          {variant.color || '-'}
                                        </TableCell>
                                        <TableCell className="text-right">
                                          {/* Costs are the client's private data
                                              and arrive null cross-org; the guard
                                              keeps the cell honest rather than
                                              relying on that. A cost typed in
                                              another currency reads "22.50 USD ≈ $32.23". */}
                                          {canSeeSkuCosts && variant.skuCostOverride != null && (
                                            <div className="flex flex-wrap items-center justify-end gap-1">
                                              <span className="tabular-nums text-sm text-muted-foreground">
                                                {formatConverted(
                                                  variant.skuCostOverride,
                                                  variant.skuCostOverrideCurrency,
                                                  variant.skuCostOverrideOrg,
                                                )}
                                              </span>
                                              <Badge variant="info" size="sm">{t('productDetail.costSourceDefault')}</Badge>
                                            </div>
                                          )}
                                        </TableCell>
                                        {showStockType && (
                                          <TableCell onClick={(e) => e.stopPropagation()}>
                                            {productCodes.length > 1 &&
                                              renderStockType(papStockTypeCodeOf(variant), isCodeless(variant))}
                                          </TableCell>
                                        )}
                                        {isPap && <TableCell />}
                                        <TableCell>
                                          <TooltipProvider>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                {/* span wrapper: a disabled button
                                                    emits no pointer events, so the
                                                    reason would never surface */}
                                                <span tabIndex={clientView ? 0 : -1}>
                                                  <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    disabled={clientView}
                                                    onClick={() => openEditVariant(variant, product.productType)}
                                                    title={clientView ? undefined : variantEditLabel(variant)}
                                                    aria-label={clientView ? managedByClient : variantEditLabel(variant)}
                                                  >
                                                    <Pencil className="h-4 w-4" />
                                                  </Button>
                                                </span>
                                              </TooltipTrigger>
                                              {clientView && (
                                                <TooltipContent side="left" className="max-w-xs">
                                                  {managedByClient}
                                                </TooltipContent>
                                              )}
                                            </Tooltip>
                                          </TooltipProvider>
                                        </TableCell>
                                      </TableRow>
                                      );
                                    })}

                                  {/* Empty variants message */}
                                  {isExpanded && product.variants.length === 0 && (
                                    <TableRow key={`${product.id}-empty`} className="bg-muted/20">
                                      {canManage && <TableCell />}
                                      <TableCell />
                                      <TableCell
                                        colSpan={(isPap ? 6 : 5) + (showStockType ? 1 : 0)}
                                        className="text-center text-sm text-muted-foreground py-4"
                                      >
                                        {t('products.noVariantsLinked')}
                                      </TableCell>
                                    </TableRow>
                                  )}
                                </>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    )}

                    {/* Ungrouped SKUs */}
                    {visibleUngrouped.length > 0 && (
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <h3 className="text-sm font-medium text-muted-foreground">
                            {t('products.ungroupedSkus', { count: visibleUngrouped.length })}
                          </h3>
                          {/* Selection action bar (own catalog only), secondary
                              actions first and the primary last (DESIGN §3.4).
                              "Group into Product" stays hidden on Trims: it always
                              creates a POD/PAP product, which has no clean home for
                              trim SKUs (see Auto-Group note above). Archive /
                              Unarchive work on every tab, trims included. Archive is
                              a reversible removal: orange outline (§3.4, §13.6).
                              Wraps rather than overflowing once the shared-blank
                              verbs join it. */}
                          {canEditCatalog && selectedSkuIds.size > 0 && (
                            <div className="flex flex-wrap items-center justify-end gap-2">
                              <span className="text-sm text-muted-foreground">
                                {t('products.selected', { count: selectedSkuIds.size })}
                              </span>
                              {/* Phase 92 (UI-SPEC 3.3): the same two verbs as
                                  the floating bar, for owner blanks that are not
                                  in a product. Same gates: this bar renders only
                                  for canEditCatalog (own catalog + skus.manage,
                                  matching requirePermission behind both
                                  dialogs), and only when this
                                  selection holds a blank the caller shares. */}
                              {sharedBlanksFromUngrouped.length > 0 && (
                                <>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setReplaceBlankOpen(true)}
                                  >
                                    <Replace className="mr-2 h-4 w-4" aria-hidden="true" />
                                    {t('sharedSkus.bulk.replaceVerb')}
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setStopOfferingOpen(true)}
                                  >
                                    <Link2Off className="mr-2 h-4 w-4" aria-hidden="true" />
                                    {t('sharedSkus.bulk.stopVerb')}
                                  </Button>
                                </>
                              )}
                              {includeArchived && ungroupedArchive.archived.length > 0 && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    setArchiveDialog({ mode: 'unarchive', targets: ungroupedArchive.archived })
                                  }
                                >
                                  <ArchiveRestore className="mr-2 h-4 w-4" aria-hidden="true" />
                                  {t('products.bulkBar.unarchive', { count: ungroupedArchive.archived.length })}
                                </Button>
                              )}
                              {ungroupedArchive.live.length > 0 && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="border-orange-300 text-orange-600 hover:bg-orange-50 hover:text-orange-700"
                                  onClick={() =>
                                    setArchiveDialog({ mode: 'archive', targets: ungroupedArchive.live })
                                  }
                                >
                                  <Archive className="mr-2 h-4 w-4" aria-hidden="true" />
                                  {t('products.bulkBar.archive', { count: ungroupedArchive.live.length })}
                                </Button>
                              )}
                              {!isTrims && (
                                <Button
                                  variant="default"
                                  size="sm"
                                  onClick={() => setGroupSkusOpen(true)}
                                >
                                  <Layers className="mr-2 h-4 w-4" />
                                  {t('products.groupIntoProduct')}
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => setSelectedSkuIds(new Set())}
                                title={t('products.clearSelection')}
                                aria-label={t('products.clearSelection')}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                        <div className="rounded-md border">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                {canManage && (!isTrims || canEditCatalog) && (
                                  <TableHead className="w-10 px-3">
                                    <Checkbox
                                      checked={
                                        visibleUngrouped.length > 0 &&
                                        selectedSkuIds.size === visibleUngrouped.length
                                      }
                                      onCheckedChange={toggleAllUngrouped}
                                      aria-label={t('products.selectAllUngroupedAria')}
                                    />
                                  </TableHead>
                                )}
                                <TableHead className="w-16">{t('table.image')}</TableHead>
                                <TableHead>{t('sku')}</TableHead>
                                <TableHead>{t('table.title')}</TableHead>
                                <TableHead>{t('size')}</TableHead>
                                <TableHead>{t('color')}</TableHead>
                                {showStockType && <TableHead className="w-48">{t('baseGarments.table.whoCanUse')}</TableHead>}
                                <TableHead className={isTrims ? 'w-44 text-right' : 'w-16'}>
                                  {t('consumables.actions')}
                                </TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {visibleUngrouped.map((sku) => {
                                // Ungrouped SKUs have no product row, so no Shopify
                                // status: only blanks (Active) and archived SKUs
                                // get a marker.
                                const skuStatus = resolveSkuStatus({
                                  retiredAt: sku.retiredAt,
                                  shopifyStatus: null,
                                  productionType: sku.productionType,
                                  isLocked: sku.isLocked,
                                  isPreprinted: sku.isPreprinted,
                                });
                                return (
                                <TableRow
                                  key={sku.id}
                                  className={cn(
                                    'hover:bg-muted/40',
                                    !clientView && 'cursor-pointer',
                                    selectedSkuIds.has(sku.id) && 'bg-muted/30',
                                    skuStatus === 'archived' && 'text-muted-foreground',
                                  )}
                                  onClick={clientView ? undefined : () => openEditUngrouped(sku)}
                                >
                                  {canManage && (!isTrims || canEditCatalog) && (
                                    <TableCell className="px-3" onClick={(e) => e.stopPropagation()}>
                                      <Checkbox
                                        checked={selectedSkuIds.has(sku.id)}
                                        onCheckedChange={() => toggleSkuSelected(sku.id)}
                                        aria-label={t('products.selectSkuAria', { sku: sku.sku })}
                                      />
                                    </TableCell>
                                  )}
                                  <TableCell className="p-2">
                                    <ProductImageWithHover
                                      imageUrl={sku.imageUrl ?? undefined}
                                      productSku={sku.sku}
                                      size="large"
                                    />
                                  </TableCell>
                                  <TableCell className="font-mono text-sm">
                                    <span className="inline-flex items-center gap-1.5">
                                      {sku.sku}
                                      <SkuStatusBadge status={skuStatus} className="font-sans" />
                                      {/* BLU-711: shared-SKU nomination. Neutral
                                          OUTLINE chip, never the brand purple —
                                          "shared" is a state, not a promotion, and
                                          purple already means pre-printed here. */}
                                      {/* F25: a blank kept for one client says so. */}
                                      {showStockType && papStockTypeCodeOf(sku) !== null ? null : sku.reservedForOrganizationId ? (
                                        <ReservedForBadge
                                          clientName={whoClientName(sku.reservedForOrganizationId)}
                                          className="shrink-0 font-sans"
                                        />
                                      ) : isSharedSku(sku) && (
                                        <Badge
                                          variant="outline"
                                          className="gap-1 text-[10px] shrink-0 font-sans"
                                          title={t('sharedSkus.badgeTitle')}
                                        >
                                          <Link2 className="h-2.5 w-2.5" aria-hidden="true" />
                                          {t('sharedSkus.badge')}
                                        </Badge>
                                      )}
                                      {sku.isPreprinted && (
                                        <span className="inline-flex items-center rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">
                                          Pre-printed
                                        </span>
                                      )}
                                      {clientView && (
                                        <ClientTechBadge
                                          tech={sku.printTechnologyResolved}
                                          source={sku.printTechnologySource}
                                          clientName={clientName}
                                          // null production_type routes as POD in batching — badge must agree.
                                          isPod={(sku.productionType ?? 'POD') === 'POD'}
                                          t={t}
                                        />
                                      )}
                                      {showWeightBadges && (
                                        <ClientWeightBadge
                                          kg={sku.shippingWeightKg}
                                          source={sku.shippingWeightSource}
                                          clientName={clientName}
                                          t={t}
                                        />
                                      )}
                                    </span>
                                  </TableCell>
                                  <TableCell className="max-w-[280px] truncate">
                                    {sku.title || '-'}
                                  </TableCell>
                                  <TableCell>{sku.size || '-'}</TableCell>
                                  <TableCell>{sku.color || '-'}</TableCell>
                                  {showStockType && (
                                    <TableCell onClick={(e) => e.stopPropagation()}>
                                      {renderStockType(papStockTypeCodeOf(sku), isCodeless(sku))}
                                    </TableCell>
                                  )}
                                  <TableCell onClick={(e) => e.stopPropagation()}>
                                    <div className={`flex items-center gap-1 ${isTrims ? 'justify-end' : ''}`}>
                                      {isTrims && canEditCatalog && (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          onClick={() =>
                                            setBulkLinkTrim({
                                              skuId: sku.id,
                                              sku: sku.sku,
                                              title: sku.title,
                                            })
                                          }
                                          className="h-8 px-2.5 text-xs gap-1.5"
                                        >
                                          <Link2 className="h-3.5 w-3.5" />
                                          {t('products.linkToProducts')}
                                        </Button>
                                      )}
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <span tabIndex={clientView ? 0 : -1}>
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                disabled={clientView}
                                                onClick={(e) => {
                                                  // The row opens it too: one open, one history entry.
                                                  e.stopPropagation();
                                                  openEditUngrouped(sku);
                                                }}
                                                title={clientView ? undefined : skuEditLabel(sku)}
                                                aria-label={clientView ? managedByClient : skuEditLabel(sku)}
                                              >
                                                <Pencil className="h-4 w-4" />
                                              </Button>
                                            </span>
                                          </TooltipTrigger>
                                          {clientView && (
                                            <TooltipContent side="left" className="max-w-xs">
                                              {managedByClient}
                                            </TooltipContent>
                                          )}
                                        </Tooltip>
                                      </TooltipProvider>
                                    </div>
                                  </TableCell>
                                </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      </div>
                    )}

                    {/* BLU-1400: the 3PL's garments, read-only */}
                    <SupplyAliasSection
                      rows={supplyAliasSkus}
                      clientView={clientView}
                      expanded={expandedProducts}
                      onToggleExpand={toggleExpand}
                    />
                  </div>
                )}

                {/* Pagination */}
                {totalProducts > 0 && (
                  <div className="flex items-center justify-between text-xs text-muted-foreground mt-4">
                    <div>
                      {t('products.showingPagination', { from: (page - 1) * PRODUCTS_PAGE_SIZE + 1, to: Math.min(page * PRODUCTS_PAGE_SIZE, totalProducts), total: totalProducts })}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page <= 1}
                      >
                        {t('common.prev')}
                      </Button>
                      <span>
                        {t('table.page', { current: page, total: totalPages })}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page >= totalPages}
                      >
                        {t('common.next')}
                      </Button>
                    </div>
                  </div>
                )}
              </TabsContent>
          </Tabs>
        </ErrorBoundary>
      </div>

      {/* Modals */}
      <EditSkuModal
        isOpen={editOpen}
        onClose={() => setEditOpen(false)}
        sku={editingSku}
        onUpdated={invalidate}
        globalMode
      />

      {/* Add trim only: blanks are added in Base garments (F24). */}
      <AddSkuModal
        isOpen={addSkuOpen}
        onClose={() => setAddSkuOpen(false)}
        onProductAdded={invalidate}
        defaultProductionType="TRIM"
      />

      <GroupSkusModal
        isOpen={groupSkusOpen}
        onClose={() => setGroupSkusOpen(false)}
        selectedSkuIds={Array.from(selectedSkuIds)}
        onGrouped={handleGrouped}
      />
      <AutoGroupModal
        isOpen={autoGroupOpen}
        onClose={() => setAutoGroupOpen(false)}
        ungroupedSkus={ungroupedSkus}
        onGrouped={handleGrouped}
      />
      {blankGroupOpen && (
        <BlankAutoGroupModal
          isOpen
          onClose={() => setBlankGroupOpen(false)}
          onGrouped={handleGrouped}
        />
      )}
      <SkuBulkEditModal
        isOpen={bulkEditOpen}
        onClose={() => setBulkEditOpen(false)}
      />

      {bulkLinkTrim && (
        <BulkLinkTrimDialog
          open
          onClose={() => setBulkLinkTrim(null)}
          componentSkuId={bulkLinkTrim.skuId}
          componentSku={bulkLinkTrim.sku}
          componentTitle={bulkLinkTrim.title}
          onLinked={invalidate}
        />
      )}

      {/* BLU-675/680: floating selection bar (shared primitive — positioning,
          live region, enter/exit motion baked in); Bulk Edit opens the variant
          batch-edit modal over the union of the selected products' variants.
          In client view the SAME bar keeps its selection semantics but swaps
          in the two actions the 3PL is allowed to take (shipping weight,
          print technology). */}
      <FloatingSelectionBar
        visible={
          canManage && (clientView ? routingVariants.length > 0 : selectedProductCount > 0)
        }
        srStatus={
          clientView
            ? t('clientCatalog.bulkBar.srStatus', { count: routingVariants.length })
            : t('products.bulkBar.srStatus', {
                products: selectedProductCount,
                variants: selectedBatchVariants.length,
              })
        }
        ariaLabel={
          clientView
            ? t('clientCatalog.bulkBar.toolbarAria')
            : t('products.bulkBar.toolbarAria')
        }
      >
        <span className="text-sm font-medium tabular-nums whitespace-nowrap">
          {clientView ? (
            t('clientCatalog.bulkBar.skuCount', { count: routingVariants.length })
          ) : (
            <>
              {t('products.bulkBar.productCount', { count: selectedProductCount })}
              <span className="text-muted-foreground font-normal">
                {' '}· {t('products.bulkBar.variantCount', { count: selectedBatchVariants.length })}
              </span>
            </>
          )}
        </span>
        <div className="mx-1.5 h-5 w-px bg-border" aria-hidden="true" />
        {!allProductsSelected && visibleProducts.length > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="rounded-full"
            onClick={toggleSelectAllProducts}
          >
            {t('products.bulkBar.selectAll', { count: visibleProducts.length })}
          </Button>
        )}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="rounded-full"
          onClick={clientView ? clearAllSelections : clearProductSelection}
        >
          {t('products.bulkBar.deselectAll')}
        </Button>
        {clientView ? (
          <>
            {/* BLU-1272: secondary before primary (DESIGN §3.4). Shipping
                weight applies to every selected SKU, POD and PAP alike.
                Print technology needs a POD SKU: without one (PAP tab,
                trims) it could only report "nothing to route", so it is
                hidden, the way Delete is hidden off the POD tab below, and
                shipping weight becomes the primary action. */}
            <Button
              type="button"
              variant={selectionHasPod ? 'ghost' : 'default'}
              size="sm"
              className="rounded-full gap-1.5"
              onClick={() => setShippingWeightOpen(true)}
              disabled={routingVariants.length === 0}
            >
              <Weight className="h-3.5 w-3.5" />
              {t('clientCatalog.bulkBar.setShippingWeight')}
            </Button>
            {selectionHasPod && (
              <Button
                type="button"
                size="sm"
                className="rounded-full gap-1.5"
                onClick={() => setPrintTechOpen(true)}
              >
                <Printer className="h-3.5 w-3.5" />
                {t('clientCatalog.bulkBar.setPrintTech')}
              </Button>
            )}
          </>
        ) : (
          <>
            {/* Who can use a blank is set per code in Base garments (F24),
                so the bar no longer marks SKUs shared. */}
            {/* Phase 92 (UI-SPEC 3.3): secondary verbs before the primary
                Bulk Edit (DESIGN §3.4), only when the selected products hold a
                blank the caller shares. SKU managers only (canEditCatalog = own
                catalog + skus.manage, matching requirePermission on every
                procedure behind them). */}
            {canEditCatalog && sharedBlanksFromProducts.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="rounded-full gap-1.5"
                onClick={() => setReplaceBlankOpen(true)}
              >
                <Replace className="h-3.5 w-3.5" aria-hidden="true" />
                {t('sharedSkus.bulk.replaceVerb')}
              </Button>
            )}
            {canEditCatalog && sharedBlanksFromProducts.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="rounded-full gap-1.5"
                onClick={() => setStopOfferingOpen(true)}
              >
                <Link2Off className="h-3.5 w-3.5" aria-hidden="true" />
                {t('sharedSkus.bulk.stopVerb')}
              </Button>
            )}
            {/* Unarchive: a secondary verb, before the primary Bulk Edit
                (DESIGN §3.4). Archive sits with Delete after the divider below.
                Own catalog only (this branch), skus.manage only (matching the
                requirePermission on retireSkus / unretireSkus). */}
            {canEditCatalog && includeArchived && productArchive.archived.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="rounded-full gap-1.5"
                onClick={() => setArchiveDialog({ mode: 'unarchive', targets: productArchive.archived })}
              >
                <ArchiveRestore className="h-3.5 w-3.5" aria-hidden="true" />
                {t('products.bulkBar.unarchive', { count: productArchive.archived.length })}
              </Button>
            )}
            <Button
              type="button"
              size="sm"
              className="rounded-full gap-1.5"
              onClick={() => setProductBatchOpen(true)}
              disabled={selectedBatchVariants.length === 0}
            >
              <Pencil className="h-3.5 w-3.5" />
              {t('products.bulkBar.bulkEdit')}
            </Button>
          </>
        )}
        {/* Removals last, behind their own divider (DESIGN §3.3/§3.4):
            Archive (reversible, orange outline) then Delete (permanent,
            destructive). Delete is POD tab only: deleteProductsGlobal refuses
            every PAP/pre-printed/locked variant, so a Delete button on the PAP
            tab would be a button whose only outcome is a refusal list. Trims
            run their own selection bar. NEVER in the BLU-709 client-catalog
            view — deleteProductsGlobal is caller-org scoped; a delete offered
            against a client's rows would target the wrong catalog. `canManage`
            is restated (the bar already gates on it) because this is the one
            irreversible action in the pill, and `skus.manage` mirrors the
            mutation's requirePermission. */}
        {(() => {
          const showArchive = canEditCatalog && productArchive.live.length > 0;
          const showDelete = canManage && !clientView && activeTab === 'pod';
          if (!showArchive && !showDelete) return null;
          return (
            <>
              <div className="mx-1.5 h-5 w-px bg-border" aria-hidden="true" />
              {showArchive && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="rounded-full gap-1.5 border-orange-300 text-orange-600 hover:bg-orange-50 hover:text-orange-700"
                  onClick={() => setArchiveDialog({ mode: 'archive', targets: productArchive.live })}
                >
                  <Archive className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('products.bulkBar.archive', { count: productArchive.live.length })}
                </Button>
              )}
              {showDelete && (
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="rounded-full gap-1.5"
                  onClick={() => setBulkDeleteOpen(true)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  {t('products.bulkBar.delete', { count: selectedProductCount })}
                </Button>
              )}
            </>
          );
        })()}
      </FloatingSelectionBar>

      {/* Own catalog only: the buttons that open it never render in client
          view, and retireSkus / unretireSkus act on the caller's org alone. */}
      <ArchiveSkusDialog
        open={archiveDialog !== null}
        onOpenChange={(next) => {
          if (!next) setArchiveDialog(null);
        }}
        mode={archiveDialog?.mode ?? 'archive'}
        targets={archiveDialog?.targets ?? []}
        onDone={finishArchive}
      />

      {/* Own catalog only: the verbs that open them never render in client
          view, and every procedure behind them acts on the caller's org alone.
          Either bar opens them on the whole shared-blank set, so a finished
          run clears both selections. */}
      {replaceBlankOpen && (
        <ReplaceBlankDialog
          open
          onOpenChange={setReplaceBlankOpen}
          selectedSkus={selectedSharedBlanks}
          onReplaced={() => {
            clearAllSelections();
            void invalidate();
          }}
        />
      )}

      {stopOfferingOpen && (
        <StopOfferingBlanksDialog
          open
          onOpenChange={setStopOfferingOpen}
          selectedSkus={selectedSharedBlanks}
          onDone={() => {
            clearAllSelections();
            void invalidate();
          }}
          onPartialFailure={() => void invalidate()}
        />
      )}

      <BulkDeleteProductsDialog
        open={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        products={selectedDeleteTargets}
        onDeleted={async () => {
          clearProductSelection();
          await invalidate();
        }}
        onPartialFailure={invalidate}
      />

      {clientOrg && (
        <PrintTechOverrideModal
          isOpen={printTechOpen}
          onClose={() => setPrintTechOpen(false)}
          targetOrganization={clientOrg}
          sharedLocations={sharedLocations}
          variants={routingVariants}
          onUpdated={clearAllSelections}
        />
      )}

      {clientOrg && (
        <ShippingWeightOverrideModal
          isOpen={shippingWeightOpen}
          onClose={() => setShippingWeightOpen(false)}
          targetOrganization={clientOrg}
          sharedLocations={sharedLocations}
          variants={routingVariants}
          defaultLocationId={data?.badgeLocationId ?? null}
          onUpdated={async () => {
            clearAllSelections();
            await invalidate();
          }}
          onPartialFailure={invalidate}
        />
      )}

      <BatchEditVariantsModal
        isOpen={productBatchOpen}
        onClose={() => setProductBatchOpen(false)}
        variants={selectedBatchVariants}
        onUpdated={async () => {
          clearProductSelection();
          await invalidate();
        }}
        onPartialFailure={invalidate}
      />

      <BackToTopPill />
    </div>
  );
}

// Client prices is its own chunk: only Admins and Coordinators of a 3PL ever
// open it, so the catalogue (every role, every visit) never downloads the grid,
// its dialogs and the CSV wizard.
const BaseGarmentsView = lazy(() =>
  import('@/features/base-garments/components/BaseGarmentsView').then((m) => ({ default: m.BaseGarmentsView })),
);

const ClientPricesView = lazy(() =>
  import('@/features/client-prices/components/ClientPricesView').then((m) => ({ default: m.ClientPricesView })),
);

/**
 * `/products` has three views on one URL (D49): the catalogue, Base garments at
 * `?view=base-garments` (the org's blank garment codes; `skus.manage`), and
 * Client prices at `?view=client-prices` (Admins edit it, Warehouse
 * Coordinators read it). RoleGuard redirects anyone else before either view
 * mounts, so they make no call. The catalogue's queries and early returns
 * never run in the other views.
 */
function ProductsRoute() {
  const [searchParams] = useSearchParams();
  const view = searchParams.get(PRODUCTS_VIEW_PARAM);
  if (view === BASE_GARMENTS_VIEW) {
    return (
      <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator']}>
        <Suspense fallback={<LoadingShell />}>
          <BaseGarmentsView />
        </Suspense>
      </RoleGuard>
    );
  }
  if (view === CLIENT_PRICES_VIEW) {
    return (
      <RoleGuard allowedRoles={['Super Admin', 'Admin', 'Warehouse Coordinator']}>
        {/* The bar alone, like every lazy route (DESIGN §15.7). */}
        <Suspense fallback={<LoadingShell />}>
          <ClientPricesView />
        </Suspense>
      </RoleGuard>
    );
  }
  return <ProductCatalogue />;
}

export default function ProductsWithErrorBoundary() {
  return (
    <PageErrorBoundary>
      <ProductsRoute />
    </PageErrorBoundary>
  );
}
